import { useCallback, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { Queue } from '@rossum/api-client/queues';
import { EmptyState } from '@rossum/components/empty-state/EmptyState';
import { Header, HeaderTitle } from '@rossum/components/header/Header';
import { PageLayout } from '@rossum/components/page-layout/PageLayout';
import {
  Autocomplete,
  Box,
  Chip,
  CircularProgress,
  Stack,
  TextField,
  Typography,
} from '@rossum/ui/material';
import { useSchema } from '../../../business/schema/useSchema';
import { assertNever } from '../../../lib/typeUtils';
import { QueueSettingsBreadcrumbs } from '../components/QueueSettingsBreadcrumbs';
import { QueueSettingsTabs } from '../components/QueueSettingsTabs';
import { ConfigMapGraph } from './ConfigMapGraph';
import { ConfigMapLegend } from './ConfigMapLegend';
import { getOptionGroup, type SearchOption } from './SearchOption';
import { SearchOptionItem } from './SearchOptionItem';
import { useConfigMapSearch } from './useConfigMapSearch';
import { useConfigurationMap } from './useConfigurationMap';
import { useNodeMaps } from './useNodeMaps';

type ConfigMapPageProps = {
  queue: Queue;
  parentPath: string;
  workspaceName: string;
};

export const ConfigMapPage = ({
  queue,
  parentPath,
  workspaceName,
}: ConfigMapPageProps) => {
  const intl = useIntl();
  const [showRules, setShowRules] = useState(true);
  const [showHooks, setShowHooks] = useState(true);

  const { data: rawData, isLoading, isError } = useConfigurationMap(queue.url);
  const { data: schema } = useSchema(queue.schema);

  // Strip edges referencing unknown node IDs — guards against future API
  // additions (new node types) producing dangling edges in the graph.
  const data = useMemo(() => {
    if (!rawData) return undefined;
    const knownIds = new Set([
      ...rawData.fields.map(f => f.id),
      ...rawData.rules.map(r => r.id),
      ...rawData.hooks.map(h => h.id),
    ]);
    return {
      ...rawData,
      edges: rawData.edges.filter(
        e => knownIds.has(e.source) && knownIds.has(e.target)
      ),
    };
  }, [rawData]);

  const hasRules = (data?.rules.length ?? 0) > 0;
  const hasHooks = (data?.hooks.length ?? 0) > 0;

  const hasNodes =
    (data?.fields.length ?? 0) +
      (data?.rules.length ?? 0) +
      (data?.hooks.length ?? 0) >
    0;

  const { nodeUrlMap } = useNodeMaps(data, schema, parentPath);
  const {
    searchChips,
    setSearchChips,
    searchInput,
    setSearchInput,
    filteredSearchOptions,
    filteredData,
  } = useConfigMapSearch(data);

  const groupByOption = useCallback(
    (option: SearchOption) => {
      const group = getOptionGroup(option.type);
      if (group === 'hook')
        return intl.formatMessage({
          id: 'containers.settings.queues.queue.overview.search.groupHooks',
        });
      if (group === 'rule')
        return intl.formatMessage({
          id: 'containers.settings.queues.queue.overview.search.groupRules',
        });
      if (group === 'field')
        return intl.formatMessage({
          id: 'containers.settings.queues.queue.overview.search.groupFields',
        });
      return assertNever(group);
    },
    [intl]
  );

  return (
    <PageLayout
      fullWidth
      renderHeader={params => (
        <Header
          scrollableDivRef={params.scrollableDivRef}
          breadcrumbs={
            <QueueSettingsBreadcrumbs
              queue={queue}
              workspaceName={workspaceName}
              breadcrumbs={[
                {
                  label: intl.formatMessage({
                    id: 'containers.settings.queues.queue.overview',
                  }),
                },
              ]}
            />
          }
          title={hasScrolled => (
            <HeaderTitle
              hasScrolled={hasScrolled}
              title={intl.formatMessage({
                id: 'containers.settings.queues.queue.overview',
              })}
            />
          )}
          description={intl.formatMessage({
            id: 'containers.settings.queues.queue.overview.description',
          })}
          tabs={<QueueSettingsTabs value="overview" parentPath={parentPath} />}
        />
      )}
    >
      <Stack
        spacing={2}
        sx={{
          flexGrow: 1,
          py: 3,
          px: 3,
        }}
      >
        {data && hasNodes && (
          <Stack direction="row" spacing={2} alignItems="center">
            <ConfigMapLegend
              showRules={showRules}
              onToggleRules={() => setShowRules(prev => !prev)}
              hasRules={hasRules}
              showHooks={showHooks}
              onToggleHooks={() => setShowHooks(prev => !prev)}
              hasHooks={hasHooks}
            />
            <Box sx={{ flex: 1 }} />
            <Autocomplete
              multiple
              size="small"
              options={filteredSearchOptions}
              groupBy={groupByOption}
              getOptionLabel={(opt: SearchOption) => opt.label}
              isOptionEqualToValue={(opt, val) => opt.id === val.id}
              // Bypass MUI's built-in filtering — handled externally in useConfigMapSearch with debounce.
              filterOptions={x => x}
              inputValue={searchInput}
              onInputChange={(_event, newValue, reason) => {
                if (reason !== 'reset') {
                  setSearchInput(newValue);
                }
              }}
              value={searchChips}
              onChange={(_event, newValue) => {
                setSearchChips(newValue);
                setSearchInput('');
              }}
              renderInput={inputParams => (
                <TextField
                  {...inputParams}
                  placeholder={
                    searchChips.length === 0
                      ? intl.formatMessage({
                          id: 'containers.settings.queues.queue.overview.searchPlaceholder',
                        })
                      : undefined
                  }
                  size="small"
                />
              )}
              renderOption={({ key, ...optionProps }, option) => (
                <SearchOptionItem
                  key={key}
                  liProps={optionProps}
                  option={option.label}
                  type={option.type}
                />
              )}
              renderTags={(tagValue, getTagProps) =>
                tagValue.map((option, index) => {
                  const { key, ...tagProps } = getTagProps({ index });
                  return (
                    <Chip
                      key={key}
                      label={option.label}
                      size="small"
                      {...tagProps}
                    />
                  );
                })
              }
              sx={{ width: 350 }}
            />
          </Stack>
        )}
        {isLoading ? (
          <Stack sx={{ py: 4, alignItems: 'center' }}>
            <CircularProgress size={48} />
          </Stack>
        ) : isError || !data ? (
          <Stack sx={{ py: 4, alignItems: 'center' }}>
            <Typography variant="body2" color="error">
              {intl.formatMessage({
                id: 'containers.settings.queues.queue.overview.error',
              })}
            </Typography>
          </Stack>
        ) : !hasNodes ? (
          <EmptyState
            title={intl.formatMessage({
              id: 'containers.settings.queues.queue.overview.emptyState.title',
            })}
            description={intl.formatMessage({
              id: 'containers.settings.queues.queue.overview.emptyState.description',
            })}
          />
        ) : (
          <ConfigMapGraph
            fields={filteredData?.fields ?? data.fields}
            edges={filteredData?.edges ?? data.edges}
            rules={filteredData?.rules ?? data.rules}
            hooks={filteredData?.hooks ?? data.hooks}
            nodeUrlMap={nodeUrlMap}
            showRules={showRules}
            showHooks={showHooks}
          />
        )}
      </Stack>
    </PageLayout>
  );
};
